"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState, useTransition } from "react";
import { unlockBook } from "@/lib/actions/social";
import { Cover } from "@/components/Cover";

export function PremiumGate({
  bookId,
  slug,
  title,
  author,
  cloth,
  ink,
  accent,
  kicker,
  signedIn,
}: {
  bookId: string;
  slug: string;
  title: string;
  author: string;
  cloth: string;
  ink: string;
  accent: string;
  kicker: string | null;
  signedIn: boolean;
}) {
  const router = useRouter();
  const [pending, start] = useTransition();
  const [error, setError] = useState(false);

  function onUnlock() {
    if (!signedIn) {
      router.push("/signin?next=" + encodeURIComponent(`/books/${slug}/read`));
      return;
    }
    start(async () => {
      setError(false);
      try {
        const res = await unlockBook(bookId, slug);
        if (res.ok) router.refresh();
        else setError(true);
      } catch {
        setError(true);
      }
    });
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-paper px-6">
      <div className="flex max-w-[520px] flex-col items-center text-center">
        <div className="mb-8 w-[150px]">
          <Cover title={title} author={author} cloth={cloth} ink={ink} accent={accent} kicker={kicker ?? undefined} />
        </div>
        <span className="mb-3 rounded-full bg-copper/15 px-3 py-1 text-[11px] font-bold uppercase tracking-[.14em] text-copper">
          Premium
        </span>
        <h1 className="font-serif text-[30px] font-semibold leading-tight tracking-tight text-ink">
          Unlock “{title}”
        </h1>
        <p className="mt-3 text-[15px] leading-relaxed text-muted">
          This is a premium rule book — every rule, its reasoning, and its
          sources. While Rulebound is in early access, premium books unlock
          free.
        </p>
        <button
          onClick={onUnlock}
          disabled={pending}
          className="mt-7 h-[50px] rounded-[12px] bg-green px-8 text-[15px] font-bold text-surface disabled:opacity-60"
          style={{ boxShadow: "var(--shadow-cta)" }}
        >
          {pending ? "Unlocking…" : signedIn ? "Unlock free & read" : "Sign in to unlock free"}
        </button>
        {error && (
          <p className="mt-3 text-[13px] font-semibold text-danger" role="alert">
            The unlock didn&apos;t go through — try again.
          </p>
        )}
        <Link href={`/books/${slug}`} className="mt-4 text-[13px] font-semibold text-muted hover:text-copper">
          ← Back to book
        </Link>
      </div>
    </div>
  );
}
